import { Checkboxes, Details } from 'nhsuk-react-components';
import { useEffect } from 'react';
import { isNewStyleRule, IterationRuleWithAndClauses } from '../../utils/ruleUtils';
import { deriveDisplayName } from '../../utils/formattingUtils';
import useReferenceData from '../../hooks/useReferenceData';

export interface RulesSectionProps {
    label: string;
    ariaLabel: string;
    rules: { Name: string; primarykey: string, iterationRule: IterationRuleWithAndClauses }[];
    disabled: boolean;
    checkBoxName: string;
    sectionCheckboxes: Record<string, boolean>;
    handleSectionCheckboxChange: (id: string) => void;
    checkedItems: Record<string, boolean>;
    handleCheckboxChange: (sectionKey: string, id: string) => void;
}

export function RulesSection({
  label,
  ariaLabel,
  rules,
  disabled,
  checkBoxName,
  sectionCheckboxes,
  handleSectionCheckboxChange,
  checkedItems,
  handleCheckboxChange,
}: RulesSectionProps) {
  const selectedItems = Object.values(checkedItems).filter(Boolean).length;

  const { referenceData } = useReferenceData();
  const operatorData = referenceData.operators;

  const getOperatorDisplayName = (operator: string | undefined) => {
    const operatorEntry = operatorData.find((op) => op.Operator === operator);
    return operatorEntry ? operatorEntry.DisplayValue.toLowerCase() : operator?.toLowerCase();
  };

  const getAttributeDescription = (r: IterationRuleWithAndClauses) => {
    if (isNewStyleRule(r)) {
      return r.Description;
    }
    return `${deriveDisplayName(r.AttributeName ?? '')} ${getOperatorDisplayName(r.Operator)} ${r.Comparator}`;
  };

  useEffect(() => {
    // eslint-disable-next-line array-callback-return
    rules.map((rule) => {
      // eslint-disable-next-line no-param-reassign
      checkedItems[rule.primarykey] = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Checkboxes.Item
      id={checkBoxName}
      aria-label={ariaLabel}
      data-testid={ariaLabel.replace(' ', '-').toLowerCase()}
      disabled={disabled}
      checked={sectionCheckboxes[checkBoxName]}
      onChange={() => handleSectionCheckboxChange(checkBoxName)}
    >
      <Details>
        <Details.Summary className="nhsuk-u-font-weight-bold">
          {label}
          {' '}
          (
          {selectedItems}
          /
          {rules.length}
          {' '}
          selected)
        </Details.Summary>
        {rules.length > 0 && (
        <Details.Text className="nhsuk-u-margin-top-5">
          {rules.map((rule, idx) => (
            <Checkboxes.Item
              key={`${rule.Name}-${idx}`} // eslint-disable-line react/no-array-index-key
              aria-label={rule.Name}
              data-testid={rule.primarykey}
              id={rule.primarykey}
              checked={checkedItems[rule.primarykey] || false}
              onChange={() => handleCheckboxChange(
                checkBoxName,
                rule.primarykey,
              )}
            >
              <div>{rule.Name}</div>
              <div>{getAttributeDescription(rule.iterationRule)}</div>
              {rule.iterationRule
                && Array.isArray(rule.iterationRule.AndClauses)
                && rule.iterationRule.AndClauses.length > 0 && (
                  <div className="nhsuk-u-margin-top-1">
                    {(rule.iterationRule).AndClauses.map((clause: IterationRuleWithAndClauses) => (
                      <div key={clause.Name}>
                        <strong>AND</strong>
                        {' '}
                        {getAttributeDescription(clause)}
                      </div>
                    ))}
                  </div>
              )}
            </Checkboxes.Item>
          ))}
        </Details.Text>
        )}
      </Details>
    </Checkboxes.Item>
  );
}
